import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import uniqby from 'lodash.uniqby';
import { useApolloClient } from 'react-apollo';

import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../../constants/album-folder-tags';
import Spinner from '../../../../../../components/global/Spinner';
import MultiSelect from '../../../../../../components/global/MultiSelect';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import {
  getTagLabelWithPrice,
  getTagsByType,
  getAlbumUniformTagOptions,
  getUniformTagOptionsFromPhotoTags,
  UNIFORM_TAG_OPTION_KIND,
} from '../../../../../../util/album-folder-tags';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { updateCustomAlbumPhotoAlbumFolderTags } from '../../../../../../util/photo';
import { getCustomAlbumAlbumFolderTags } from '../../../../../../util/album';
import {
  moveTeamSplitPhotos,
  getTeamSplitRefetchQueries,
} from '../../AlbumPhotos/hooks/useTeamSplitPhotos';
import { ALBUM_PHOTO_FILTERS } from '../../../../../../constants/albums';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import useGlobalToast from '../../../../../../hooks/useGlobalToast';
import MoneyIcon from '../../../../../../components/icons/MoneyIcon';

const propTypes = {
  loading: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    album_folder_tags: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          album_folder_tag: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.oneOf(Object.values(ALBUM_FOLDER_TAG_TYPE))
              .isRequired,
          }),
          teams: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                team: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                }),
              }),
            ),
          }),
          levels: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                level: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string,
                }),
              }),
            ),
          }),
        }),
      ),
    }),
  }),
  selectedPhotos: PropTypes.arrayOf(PropTypes.string),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    album_folder_tags: PropTypes.shape({
      records: PropTypes.arrayOf(PropTypes.object),
    }),
    root_album_folder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      tags: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            type: PropTypes.oneOf(Object.values(ALBUM_FOLDER_TAG_TYPE))
              .isRequired,
            levels: PropTypes.shape({
              records: PropTypes.arrayOf(PropTypes.object),
            }),
          }),
        ),
      }),
    }),
  }),
  createAlbumFolderTag: PropTypes.func.isRequired,
  albumPhotosAlbumFolderTagsCreate: PropTypes.func.isRequired,
  albumPhotoAlbumFolderTagsDelete: PropTypes.func.isRequired,
  photoGroupId: PropTypes.string,
  isSelectAll: PropTypes.bool,
  albumPhotoFilter: PropTypes.oneOf(Object.values(ALBUM_PHOTO_FILTERS)),
  uploaderIds: PropTypes.arrayOf(PropTypes.string),
  filenameSearchQuery: PropTypes.string,
  objectSearchQuery: PropTypes.string,
  teamTagId: PropTypes.string,
  teamSplitVarsBase: PropTypes.object,
  onRequestClose: PropTypes.func,
};

const PhotoAlbumFolderTagsForm = ({
  loading,
  photo,
  selectedPhotos,
  album,
  albumPhotosAlbumFolderTagsCreate,
  albumPhotoAlbumFolderTagsDelete,
  photoGroupId,
  isSelectAll,
  albumPhotoFilter,
  uploaderIds,
  filenameSearchQuery,
  objectSearchQuery,
  teamTagId,
  teamSplitVarsBase,
  createAlbumFolderTag,
  onRequestClose,
}) => {
  const client = useApolloClient();
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedMatchingTag, setSelectedMatchingTag] = useState('');
  const [customTags, setCustomTags] = useState([]);
  // Level and team rows of the uniform tag picker, mixed.
  const [uniformTags, setUniformTags] = useState([]);
  const [rootAlbumFolderTags, setRootAlbumFolderTags] = useState([]);
  const [rootAlbumFolderTagsSuccess, setRootAlbumFolderTagsSuccess] =
    useState(false);
  const openAlert = useOpenAlert();
  const { setToast } = useGlobalToast();

  const matchingTagsEnabled = album?.matching_tags_enabled;
  const rootAlbumFolder = album?.root_album_folder || {};
  const rootAlbumFolderTagsRecords = (
    rootAlbumFolder?.tags?.records ?? []
  ).filter(albumFolderTag => {
    // Only return matching tags if the Album has them
    // enabled as a feature.
    if (matchingTagsEnabled) {
      return true;
    }

    return !albumFolderTag.is_matching_tag;
  });
  const newMatchingTagLabel = 'Deliver to All';
  // Only the levels and teams assigned to this album, so a photo cannot be
  // tagged with one the album does not carry.
  const uniformTagOptionGroups = getAlbumUniformTagOptions(
    rootAlbumFolderTagsRecords,
    album?.album_folder_tags?.records ?? [],
  );

  const createTag = async (tagLabel, isMatchingTag, autoSelect) => {
    try {
      setCreating(true);
      setError('');

      const {
        data: { albumFolderTagCreate },
      } = await createAlbumFolderTag(
        rootAlbumFolder.id,
        null,
        ALBUM_FOLDER_TAG_TYPE.CUSTOM,
        tagLabel,
        null,
        isMatchingTag,
      );

      if (autoSelect) {
        setCustomTags([
          {
            value: albumFolderTagCreate.album_folder_tag.id,
            label: albumFolderTagCreate.album_folder_tag.name,
            isPricingTag: albumFolderTagCreate.album_folder_tag.is_pricing_tag,
          },
          ...customTags,
        ]);
      }
    } catch (responseError) {
      setError(getErrorString(responseError));
    } finally {
      setRootAlbumFolderTagsSuccess(false);
      setCreating(false);
    }
  };

  useEffect(() => {
    if (album && !rootAlbumFolderTagsSuccess) {
      const customTagRecords = getTagsByType(
        ALBUM_FOLDER_TAG_TYPE.CUSTOM,
        rootAlbumFolderTagsRecords,
      );

      setRootAlbumFolderTags(customTagRecords);
      setRootAlbumFolderTagsSuccess(true);
    }
  }, [rootAlbumFolderTagsRecords.length, album?.id]);

  useEffect(() => {
    if (rootAlbumFolderTagsSuccess) {
      const matchingTags = rootAlbumFolderTags.filter(
        tag => tag.is_matching_tag,
      );

      if (
        !matchingTags.length &&
        !rootAlbumFolder.matching_disabled &&
        matchingTagsEnabled
      ) {
        createTag(newMatchingTagLabel, true);
      }
    }
  }, [rootAlbumFolderTagsSuccess]);

  useEffect(() => {
    const albumFolderTagsRecords = photo?.album_folder_tags?.records || [];
    const albumAlbumFolderCustomTags = getCustomAlbumAlbumFolderTags(
      albumFolderTagsRecords,
    ).map(tag => ({
      value: tag.album_folder_tag.id,
      label: getTagLabelWithPrice(tag.album_folder_tag),
      isPricingTag: tag?.album_folder_tag?.is_pricing_tag,
    }));
    const matchingTags = albumFolderTagsRecords.filter(
      tag => tag.album_folder_tag.is_matching_tag,
    );

    if (matchingTags.length) {
      setSelectedMatchingTag(matchingTags[0].album_folder_tag.name);
    }

    setCustomTags(
      uniqby(
        [...customTags, ...albumAlbumFolderCustomTags],
        ({ value }) => value,
      ),
    );
    setUniformTags(
      uniqby(
        [
          ...uniformTags,
          ...getUniformTagOptionsFromPhotoTags(albumFolderTagsRecords),
        ],
        ({ kind, value }) => `${kind}:${value}`,
      ),
    );
  }, [photo]);

  const onCustomTagsChange = async values => {
    // We can distinguish newly added custom tags in ReactSelect
    // because they have the same label and value.
    const newTag = values.find(({ label, value }) => label === value);
    const matchingTags = values.filter(tag => tag.isMatchingTag);

    // If we detect a new custom tag we have to create it on
    // the album folder level first
    if (newTag) {
      await createTag(newTag.label, false, true);
      return;
    }

    if (matchingTags.length && !selectedMatchingTag) {
      const selectedTag = matchingTags[0].label;
      openAlert({
        type: 'info',
        message: (
          <p>
            NOTE: You have selected the “
            <span className="text-primary">{selectedTag}</span>” tag. When
            applied, this photo will appear as a matched photo for everyone
            joined to this gallery.
          </p>
        ),
      });
      setSelectedMatchingTag(selectedTag);
    } else if (!matchingTags.length) {
      setSelectedMatchingTag('');
    }

    setCustomTags(values);
  };

  const onUniformTagsChange = values => {
    setUniformTags(values || []);
  };

  const onFormSubmit = async event => {
    event.preventDefault();

    try {
      setSubmitting(true);

      // Every eligible tag gets an entry (even an empty one), so removing a
      // tag's last level or team still produces its delete + bare re-create.
      // Tags already on the photo are included even when the album no longer
      // offers their levels, otherwise clearing those would silently no-op.
      const eligibleUniformTagIds = [
        ...new Set([
          ...uniformTagOptionGroups.flatMap(({ options }) =>
            options.map(({ tagId }) => tagId),
          ),
          ...(photo?.album_folder_tags?.records ?? [])
            .filter(
              record =>
                record.album_folder_tag?.type === ALBUM_FOLDER_TAG_TYPE.UNIFORM,
            )
            .map(record => record.album_folder_tag.id),
        ]),
      ];
      const selectedIdsOfKind = (tagId, kind) =>
        uniformTags
          .filter(option => option.tagId === tagId && option.kind === kind)
          .map(({ value }) => value);
      const uniformTagSelections = Object.fromEntries(
        eligibleUniformTagIds.map(tagId => [
          tagId,
          {
            levelIds: selectedIdsOfKind(tagId, UNIFORM_TAG_OPTION_KIND.LEVEL),
            teamIds: selectedIdsOfKind(tagId, UNIFORM_TAG_OPTION_KIND.TEAM),
          },
        ]),
      );

      await updateCustomAlbumPhotoAlbumFolderTags({
        photoIds: photo ? [photo.id] : selectedPhotos,
        albumId: album.id,
        albumPhotoAlbumFolderTags: photo?.album_folder_tags.records ?? [],
        customTagIds: customTags.map(tag => tag.value),
        uniformTagSelections,
        createTags: albumPhotosAlbumFolderTagsCreate,
        deleteTags: photo ? albumPhotoAlbumFolderTagsDelete : undefined,
        // Used for select all within albumPhotosAlbumFolderTagsCreate
        photoGroupId,
        isSelectAll,
        albumPhotoFilter,
        uploaderIds,
        filenameSearchQuery,
        objectSearchQuery,
      });

      // If the TEAM tag membership changed, move the affected photos between the
      // TEAM/INDIVIDUAL split grids in-place (no-op when the split is inactive).
      if (teamTagId) {
        const willBeTeam = customTags.some(({ value }) => value === teamTagId);
        const photoIds = photo ? [photo.id] : selectedPhotos;

        if (photo) {
          const wasTeam = (photo.album_folder_tags?.records ?? []).some(
            ({ album_folder_tag: albumFolderTag }) =>
              albumFolderTag.id === teamTagId,
          );

          if (wasTeam !== willBeTeam) {
            moveTeamSplitPhotos(client, {
              teamTagId,
              splitVarsBase: teamSplitVarsBase,
              photoIds,
              toTeam: willBeTeam,
            });
          }
        } else if (willBeTeam && isSelectAll) {
          // Under select-all, `selectedPhotos` holds the *ignored* ids, so the
          // exact moved set is not derivable client-side; reconcile the split
          // grids with a refetch instead of an in-place move.
          await Promise.all(
            getTeamSplitRefetchQueries({
              teamTagId,
              splitVarsBase: teamSplitVarsBase,
            }).map(({ query, variables }) =>
              client.query({ query, variables, fetchPolicy: 'network-only' }),
            ),
          );
        } else if (willBeTeam) {
          // Bulk edits only add tags, so photos can only move into the team grid.
          moveTeamSplitPhotos(client, {
            teamTagId,
            splitVarsBase: teamSplitVarsBase,
            photoIds,
            toTeam: true,
          });
        }
      }

      setToast('Tags saved successfully!');
      onRequestClose?.();
    } catch (responseError) {
      setError(getErrorString(responseError));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !album || (!isSelectAll && !selectedPhotos && !photo)) {
    return (
      <div className="text-center my-3">
        <Spinner />
      </div>
    );
  }

  return (
    <form
      method="post"
      onSubmit={onFormSubmit}
      className="photo-album-folder-tags-modal"
    >
      {error && <Alert type="danger">{error}</Alert>}

      <div className="mb-4">
        <MultiSelect
          id="custom-tags"
          className="photo-album-folder-tags-modal-select"
          label="Add Custom Tags"
          placeholder="Add a custom tag..."
          labelTip="Type and hit enter to select or create a new Custom Tag."
          isMulti
          creatable
          isLoading={creating || submitting}
          value={customTags}
          noOptionsMessage={() =>
            'No custom tags found, type and hit enter to create a new one.'
          }
          options={rootAlbumFolderTags.map(customTag => ({
            value: customTag.id,
            isMatchingTag: customTag.is_matching_tag,
            isPricingTag: customTag?.is_pricing_tag,
            label: getTagLabelWithPrice(customTag),
          }))}
          formatOptionLabel={(option, { context }) => {
            // Photo searches have `album_folder_tag`, while
            if (context === 'value') {
              if (option.isPricingTag) {
                return (
                  <div className="d-flex align-items-center">
                    {option.label}&nbsp;
                    <MoneyIcon color="#0d84fe" size={14} />
                  </div>
                );
              }

              return option.label;
            }

            return (
              <div className="d-flex align-items-center">
                {option?.label}
                {!!option.isPricingTag && (
                  <MoneyIcon color="#0d84fe" className="ml-1" size={16} />
                )}
              </div>
            );
          }}
          onChange={onCustomTagsChange}
        />
        <div className="photo-album-folder-tags-modal-description">
          Custom tags allow you to search and find photos more easily.
        </div>
        {selectedMatchingTag && (
          <div className="photo-album-folder-tags-modal-tip">
            <span className="text-extra-bold">NOTE:</span> You have selected the
            “<span className="text-primary">{selectedMatchingTag}</span>” tag.
            When applied, this photo will appear as a matched photo for everyone
            joined to this gallery.
          </div>
        )}
      </div>
      {uniformTagOptionGroups.length > 0 && (
        <div className="mb-4">
          <MultiSelect
            id="uniform-tags"
            className="photo-album-folder-tags-modal-select"
            label="Add Level & Team Tags"
            placeholder="Add a level or team tag..."
            labelTip="Select a level, or a team within it, assigned to this album."
            isMulti
            isLoading={submitting}
            value={uniformTags}
            noOptionsMessage={() => 'No levels assigned to this album.'}
            options={uniformTagOptionGroups}
            formatOptionLabel={(option, { context }) => {
              // Selected chips carry the gender prefix; menu options don't,
              // since they sit under their gender group heading.
              if (context === 'value') {
                return option.chipLabel ?? option.label;
              }

              // A level row heads the team rows listed right under it.
              return option.kind === UNIFORM_TAG_OPTION_KIND.LEVEL ? (
                <span className="text-bold">{option.label}</span>
              ) : (
                option.label
              );
            }}
            onChange={onUniformTagsChange}
          />
          <div className="photo-album-folder-tags-modal-description">
            A level tag matches this photo to everyone who has joined that
            level, on any of its teams. A team tag matches only that team.
          </div>
        </div>
      )}
      <div className="text-center">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
};

PhotoAlbumFolderTagsForm.propTypes = propTypes;

export default PhotoAlbumFolderTagsForm;
